import { useState, useEffect } from "react";
import dynamic from "next/dynamic";
import App from "next/app";
import type { AppContext, AppProps } from "next/app";
import type { NextComponentType } from "next";
import { appWithTranslation } from "next-i18next";
import { Provider } from "urql";
import { ErrorBoundary } from "@appsignal/react";
import { useRouter } from "next/router";
import { LicenseInfo } from "@mui/x-license";

// custom
import { urqlClient } from "lib/graphqlClient";
import { ReleaseGuardProvider } from "lib/release/ReleaseGuardProvider";
import { MeFieldFragment } from "graphql/generated";
import { AnalyticsTags, usePageView } from "lib/AnalyticsTags";
import { appsignal, appsignalTags } from "lib/appsignal";
import { MeContext } from "lib/auth";
import { DictContext } from "lib/dict";
import { SelectedTenantContext } from "lib/selectedTenant";
import { LoadingProvider, Loading } from "lib/loadingContext";
import JotaiProvider from "lib/jotaiProvider";
import { parseAcceptLanguage } from "lib/parseAcceptLanguage";
import "../styles/nitoel.css";
import theme from "lib/theme";
import createEmotionCache from "lib/createEmotionCache";
import PageHead from "components/atoms/PageHead";
import Favicon from "components/atoms/Favicon";

const GlobalHeader = dynamic(() => import("components/GlobalHeader"), { ssr: false });

// mui
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { CacheProvider, EmotionCache } from "@emotion/react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

type DictContentType = Record<string, unknown>;

// MUI premiumのライセンスキーを設定
LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_PREMIUM_LICENSE_KEY || "no-license-key");

const FallbackComponent = () => <div>Sorry... There was something wrong. Please reload or contact to administrator.</div>;

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
  pageProps: {
    me: MeFieldFragment;
    dict: DictContentType;
    layout: string | null;
    primaryLang: string;
  };
}

const PageLoading: React.FC<{ pageLoading: boolean }> = ({ pageLoading }) => {
  return (
    <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }} open={pageLoading}>
      <CircularProgress color="inherit" />
    </Backdrop>
  );
};

const MyApp: NextComponentType<AppContext, any, MyAppProps> = (props) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const router = useRouter();
  usePageView(); // GA用のイベントフックを仕込む。

  // メンテナンスページへの強制リダイレクト
  // useEffect(() => {
  //   if (router.pathname !== "/maintenance" && router.pathname !== "/404" && router.pathname !== "/_error") {
  //     router.replace("/maintenance");
  //   }
  // }, [router]);

  // ユーザーの言語設定に応じてhtml lang属性を設定
  // ブラウザの翻訳機能が正しく動作するようにするため
  useEffect(() => {
    const lang = pageProps.me?.languageCode || "ja";
    document.documentElement.lang = lang;
  }, [pageProps.me?.languageCode]);

  // https://github.com/vercel/next.js/blob/canary/examples/with-loading/pages/_app.tsx
  // を参考に。画面遷移時にLoadingするようにするための処理。
  const [pageLoading, setPageLoading] = useState(false);
  useEffect(() => {
    const handleStart = () => {
      setPageLoading(true);
    };

    const handleStop = () => {
      setPageLoading(false);
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleStop);
    router.events.on("routeChangeError", handleStop);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleStop);
      router.events.off("routeChangeError", handleStop);
    };
  }, [router]);

  let content = null;
  if (pageProps.layout === "none") {
    content = <Component {...pageProps} />;
    // ログインしてないけど、nitoel的な装飾をしたいページ用
  } else if (pageProps.layout === "noLoginHeader") {
    content = (
      <GlobalHeader noMenu>
        <PageLoading pageLoading={pageLoading} />
        <Component {...pageProps} />
      </GlobalHeader>
    );
  } else {
    content = (
      <GlobalHeader>
        <PageLoading pageLoading={pageLoading} />
        <Component {...pageProps} />
      </GlobalHeader>
    );
  }

  return (
    <ErrorBoundary
      instance={appsignal}
      tags={appsignalTags(pageProps.me, router.pathname)}
      fallback={() => <FallbackComponent />}
    >
      <Provider value={urqlClient}>
        <CacheProvider value={emotionCache}>
          <ThemeProvider theme={theme}>
            <MeContext.Provider value={pageProps.me}>
              <DictContext.Provider
                value={{ dict: pageProps.dict, replacements: {}, debug: router.query.debug_dict != undefined }}
              >
                <SelectedTenantContext.Provider value={pageProps.me?.selectedTenant}>
                  {/* 公開版の確認と更新案内（lib/release/ReleaseGuardProvider.tsx）。案内の文言は dict の common を使う */}
                  <ReleaseGuardProvider>
                    <JotaiProvider>
                      <CssBaseline />
                      <LoadingProvider>
                        {/*
                        各ページ毎にPageTitleやPageHeadコンポーネントを追加してtitleタグを設定してください。
                        以下のPageHeadコンポーネントは、titleタグの設定漏れがあったためのもので、デフォルト値（ニトエル）を定義しています。
                        各ページ毎にtitleタグが設定されている場合、公式ドキュメントでは重複するtitleタグはチェックすると記載されています。
                        そのチェック後の挙動については明示されていませんが、検証したところ、後から描画されるものが優先される挙動でした。
                        ref: https://nextjs.org/docs/pages/api-reference/components/head#avoid-duplicated-tags

                        また、primaryLangはDBのロケールではなく、HTTPのAccept-Languageリクエストヘッダーのロケールをセットします。
                      */}
                        <PageHead primaryLang={pageProps.primaryLang} />
                        <Favicon />
                        <AnalyticsTags />
                        <Loading />
                        {content}
                      </LoadingProvider>
                    </JotaiProvider>
                  </ReleaseGuardProvider>
                </SelectedTenantContext.Provider>
              </DictContext.Provider>
            </MeContext.Provider>
          </ThemeProvider>
        </CacheProvider>
      </Provider>
    </ErrorBoundary>
  );
};

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext);

  // DBのロケールではなく、HTTPのAccept-Languageリクエストヘッダーのロケールを取得します。
  const acceptLang = appContext.ctx.req?.headers["accept-language"];
  const primaryLang = parseAcceptLanguage(acceptLang);

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      primaryLang: primaryLang,
    },
  };
};

export default appWithTranslation(MyApp as React.FC);
